import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { AppState } from "@app/app.reducer";
import { PhoneNumberService } from "@app/core/services/phone-number/phone-number.service";
import { markAllAsTouched } from "@app/shared/utils/form-utils";
import { Store } from "@ngrx/store";
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  first,
  map,
  of,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";
import * as toastActions from "@app/core/components/toast/ngrx/toast.actions";
import { SoderbergPartnerService } from "@app/sidebar/potential-buyer/services/soderberg-partner.service";

@Component({
  selector: "app-send-tips-to-sop-modal",
  templateUrl: "./send-tips-to-sop-modal.component.html",
  styleUrls: ["./send-tips-to-sop-modal.component.scss"],
})
export class SendTipsToSopModalComponent implements OnInit, OnDestroy {
  form: FormGroup;
  unsubscribe$ = new Subject<void>();
  isWorking$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isLeadSentToSoderberg$ = new BehaviorSubject("unknown");

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<SendTipsToSopModalComponent>,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private soderbergPartnerService: SoderbergPartnerService,
    public phoneNumberService: PhoneNumberService
  ) {
    this.form = this.fb.group({
      eaOid: [null, Validators.required],
      contactId: [null, Validators.required],
      comment: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.mapStateToProps();
    this.fillForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  closeModal() {
    this.dialogRef.close();
  }

  submit() {
    if (this.form.invalid) {
      markAllAsTouched(this.form);
      return;
    }

    this.isWorking$.next(true);
    const eaOid = this.form.get("eaOid").value;
    const contactId = this.form.get("contactId").value;
    const comment = this.form.get("comment").value;

    this.soderbergPartnerService
      .sendLead(contactId, eaOid, comment)
      .pipe(
        first(),
        catchError(() => {
          this.store.dispatch(
            toastActions.danger({
              message: "lead_sent_to_soderberg_failed",
            })
          );
          this.isWorking$.next(false);
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.store.dispatch(
          toastActions.success({
            message: "lead_sent_to_soderberg_success",
            duration: 8000,
          })
        );
        this.isWorking$.next(false);
        this.closeModal();
      });
  }

  private mapStateToProps() {
    this.form
      .get("eaOid")
      .valueChanges.pipe(
        switchMap((eaOid) => {
          if (!eaOid) {
            return of("unknown");
          }

          return this.soderbergPartnerService
            .getSentLeadStatus(this.form.get("contactId").value, eaOid)
            .pipe(
              first(),
              map((sentLeadStatus) => (sentLeadStatus ? "sent" : "not_sent"))
            );
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((data) => {
        this.isLeadSentToSoderberg$.next(data);
      });
  }

  private fillForm() {
    this.form.get("contactId").setValue(this.data?.contact?.contactId);
    this.form.get("eaOid").setValue(this.data?.eaOid);
  }
}
